import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export interface SubmitOrderInput {
  customer_id: string;
  customer_location_id: string;
  lines: Array<{ catalog_item_id: string; quantity: number }>;
  po_number?: string;
  delivery_notes?: string;
  requested_delivery_date?: string;
  /** 'delivery' (default) or 'will_call' — will-call carries the pickup
   * date in requested_delivery_date. */
  fulfillment?: 'delivery' | 'will_call';
  idempotency_key?: string;
  dry_run?: boolean;
  /** Superadmin-only: bypass the credit hold (server re-verifies + audit-logs). */
  override_hold?: boolean;
}

export interface SubmitOrderResult {
  ok: boolean;
  dry_run?: boolean;
  fulfillment?: 'delivery' | 'will_call';
  pickup_date?: string | null;
  order_id?: string;
  order_number?: string | null;
  sf_job_id?: number | string;
  sf_job_number?: string;
  status?: string;
  totals?: {
    subtotal: number;
    list_subtotal: number;
    fee_total?: number;
    estimated_total: number;
  };
  /** Auto-added fee lines (fuel surcharge / DOT hazmat / SSB beverage tax). */
  fee_lines?: Array<{
    qboItemId: number;
    name: string;
    quantity: number;
    fee: number;
    amount: number;
    kind: 'fuel' | 'hazmat' | 'force_majeure' | 'crv' | 'wa_syrup' | 'ssb' | 'sea_ssbt';
  }>;
  sf_payload?: unknown;
  sf_error?: string;
  error?: string;
  /** Machine-readable failure reason. 'ordering_locked' = account on a
   * manual credit hold; 'account_inactive' = customer deactivated. Both
   * are surfaced to the user verbatim and must NOT go through the
   * lost-response recovery path. */
  code?: string;
  customer?: { id: string; name: string; sf_customer_id: number | null };
  location?: { id: string; display_name: string };
  order_lines?: unknown[];
  /** Set by the recovery path when the HTTP response was lost but a
   * matching submitted order was found in the orders table within the
   * last ~90s. The order_id + sf_job_id are still trustworthy. */
  recovered?: boolean;
}

/**
 * Recovery: look up the user's most recently submitted order in the last
 * 90 seconds. If one exists with a non-null sf_job_id, the backend
 * actually succeeded — the HTTP response was just lost in transit.
 */
async function findRecentlySubmittedOrder(
  customerId: string,
  customerLocationId: string,
): Promise<{ order_id: string; sf_job_id: string | null; order_number: string | null } | null> {
  const sinceIso = new Date(Date.now() - 90_000).toISOString();
  const { data, error } = await supabase
    .schema('orders')
    .from('orders')
    .select('id, sf_job_id, order_number, submitted_at, status')
    .eq('customer_id', customerId)
    .eq('customer_location_id', customerLocationId)
    .eq('status', 'submitted')
    .gte('submitted_at', sinceIso)
    .order('submitted_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error || !data) return null;
  return {
    order_id: data.id as string,
    sf_job_id: (data.sf_job_id as string | null) ?? null,
    order_number: (data.order_number as string | null) ?? null,
  };
}

/**
 * useSubmitOrder — sends the cart to the submit-order Netlify function
 * with the caller's Supabase JWT. The function verifies the user owns
 * the customer, builds the SF Job body, persists locally, and (unless
 * dry_run) POSTs to SF.
 */
export function useSubmitOrder() {
  const qc = useQueryClient();
  return useMutation<SubmitOrderResult, Error, SubmitOrderInput>({
    // A real order spends the one order allowed while a payment clears, so the
    // pass has to be re-read rather than trusted from a 30-second cache.
    onSettled: () => { void qc.invalidateQueries({ queryKey: ['order-pass'] }); },
    mutationFn: async (input) => {
      const { data: session } = await supabase.auth.getSession();
      const jwt = session.session?.access_token;
      if (!jwt) throw new Error('Not signed in.');

      let res: Response;
      let rawText: string;
      try {
        res = await fetch('/.netlify/functions/submit-order', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${jwt}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(input),
        });
        rawText = await res.text();
      } catch (netErr) {
        // True network failure (request never completed). On a real
        // submit, try the recovery path in case the function actually
        // ran on the backend before the socket closed.
        if (!input.dry_run) {
          const found = await findRecentlySubmittedOrder(
            input.customer_id,
            input.customer_location_id,
          );
          if (found) {
            return {
              ok: true,
              recovered: true,
              order_id: found.order_id,
              order_number: found.order_number,
              sf_job_id: found.sf_job_id ?? undefined,
              status: 'submitted',
            };
          }
        }
        throw netErr instanceof Error
          ? netErr
          : new Error(String(netErr));
      }

      // Parse the body safely. Empty body or invalid JSON triggers
      // the same recovery path.
      let data: SubmitOrderResult | null = null;
      if (rawText && rawText.trim().length > 0) {
        try {
          data = JSON.parse(rawText) as SubmitOrderResult;
        } catch {
          // fall through to recovery
        }
      }

      if (data && data.ok !== false && res.ok) {
        return data;
      }

      // Hard, deterministic server rejections (ordering locked / account
      // inactive) are NOT lost responses — surface the message directly and
      // never run the recovery poll (which could otherwise misattribute an
      // older order as this submit's success).
      if (
        data &&
        (data.code === 'ordering_locked' || data.code === 'account_inactive')
      ) {
        throw new Error(data.error ?? 'Ordering is not available on this account.');
      }

      // Anything else (empty body, parse fail, ok=false, !res.ok) — on a
      // real submit, check whether the order actually landed.
      if (!input.dry_run) {
        const found = await findRecentlySubmittedOrder(
          input.customer_id,
          input.customer_location_id,
        );
        if (found) {
          return {
            ok: true,
            recovered: true,
            order_id: found.order_id,
            order_number: found.order_number,
            sf_job_id: found.sf_job_id ?? undefined,
            status: 'submitted',
          };
        }
      }

      // Genuine failure. Surface the best error message we have. When the
      // backend sends both the headline (`error`) and the Service Fusion
      // detail (`sf_error`), show both — the detail is what makes an SF
      // rejection (bad product name, unknown customer, …) actionable.
      const fallbackMsg =
        data?.error && data?.sf_error
          ? `${data.error} — ${data.sf_error}`
          : (data?.error ??
            data?.sf_error ??
            (rawText && rawText.length < 300 ? rawText : `HTTP ${res.status}`));
      throw new Error(fallbackMsg);
    },
  });
}
